import {
    GetNftRewardResponse,
    NftOwnership,
    NftsApi,
    XpMultiplier,
} from "@devour/client";
import {Dispatch, ReactElement, SetStateAction, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Toast from "@/components/Toast";
import classNames from "classnames";
import {IStore} from "@/redux/defaultStore";
import {addError} from "@/redux/meta/metaActions";
import {useGetRedeemableNftOwnerships} from "@/hooks/useGetRedeemableNftOwnerships";
import {useGetNftOwnershipsForUser} from "@/hooks/useGetNftOwnershipsForUser";
import getConfig from "@/utils/getConfig";
import {useGetNftOwnerships} from "@/hooks/useGetNftOwnerships";
import isLootboxUnburnt from "@/utils/isLootboxUnburnt";
import {parseChain} from "@/utils/parseChain";
import {RewardNftRedemptionState} from "@/components/goVip/GoVipRewardsSelectedNft";
import {useGetNftMetadata} from "@/hooks/useGetNftMetadata";
import GoVipRewardsSelectedXpMultiplierFooter
    from "@/components/goVip/GoVipRewardsSelectedXpMultiplierFooter";
import moment from "moment";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";

interface Props {
    reward: GetNftRewardResponse;
    ownership: NftOwnership;
    toggleDrawer: () => void;
    setSelectedNft: Dispatch<SetStateAction<NftOwnership>>;
}

function GoVipRewardsSelectedXpMultiplier(props: Props) {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {reward} = props;
    const rewardMetadata = reward.nftReward;

    const [showToast, setShowToast] = useState<boolean>(false);
    const [prizeWon, setPrizeWon] = useState<XpMultiplier>(undefined);
    const [redemptionState, setRedemptionState] = useState<RewardNftRedemptionState>(RewardNftRedemptionState.OPENING);
    const [revealPrize, setRevealPrize] = useState<boolean>(false);
    const {
        refetch: refetchRedeemableNftOwnerships,
    } = useGetRedeemableNftOwnerships(fullToken);
    const {data: nftMetadata} = useGetNftMetadata(props.ownership.id, fullToken);
    const {refetch: refetchNftOwnershipsForUser} = useGetNftOwnershipsForUser(fullToken);
    const {refetch: refetchNftOwnerships} = useGetNftOwnerships(fullToken);
    const {refetch: refetchUserProfile} = useGetUserProfile(fullToken);
    const sparklingEffectTimeout: number = rewardMetadata.transitionEffectEnabled ? 1500 : 0;
    const rewardImage =
				nftMetadata?.tokenImageUrl ?? `${import.meta.env.VITE_CDN_URL}/images/xp.webp`;

    useEffect(() => {
        resetProgress();
    }, [props.ownership]);

    function resetProgress(): void {
        setPrizeWon(undefined);
        if (isLootboxUnburnt(props.ownership)) {
            setRedemptionState(RewardNftRedemptionState.NOT_INITIATED);
        } else {
            setRedemptionState(RewardNftRedemptionState.OPENING);
        }
        setRevealPrize(false);
        setShowToast(false);
    }

    async function onConfirmRedeem(): Promise<void> {
        setShowToast(true);
        setRedemptionState(RewardNftRedemptionState.IN_PROGRESS);
        await new NftsApi(getConfig(fullToken)).burnNftReward({
            id: props.ownership?.id,
        });
        void refetchRedeemableNftOwnerships();
        void refetchNftOwnerships();
        void refetchNftOwnershipsForUser();
    }

    function onTransactionSent(transactionHash: `0x${string}`): void {
        void new NftsApi(getConfig(fullToken)).burnNftReward({
            id: props.ownership?.id,
            burnNftRewardBody: {
                transactionHash,
            },
        });
    }

    async function onTransactionConfirmed(transactionHash?: `0x${string}`): Promise<void> {
        try {
            const prizeWon = await new NftsApi(getConfig(fullToken)).redeemXpMultiplier({
                redeemXpMultiplierBody: {
                    transactionHash: transactionHash,
                    nftOwnershipId: props.ownership?.id,
                },
            });
            setRevealPrize(true);
            setShowToast(false);
            setTimeout(() => {
                setPrizeWon(prizeWon);
                setRedemptionState(RewardNftRedemptionState.DONE);
            }, sparklingEffectTimeout);
        } catch (e) {
            resetProgress();
            dispatch(await addError(e));
        } finally {
            void refetchRedeemableNftOwnerships();
            void refetchNftOwnerships();
            void refetchNftOwnershipsForUser();
            void refetchUserProfile();
        }
    }

    async function onVirtualRedemption(): Promise<void> {
        setRedemptionState(RewardNftRedemptionState.IN_PROGRESS);
        await onTransactionConfirmed();
    }

    function handleToastDismissal(): void {
        setShowToast(false);
    }

    function renderPrizeInfo(): ReactElement {
        return (
            <div className="govip-rewards-drawer_reward_info">
                <div className="govip-rewards-drawer_reward_info_origin">
                    <span>XP Multiplier Applied</span>
                </div>
                <div className="govip-rewards-drawer_reward_info_title">{prizeWon.name}</div>
                <div
                    className="govip-rewards-drawer_reward_info_description">
                    Your {prizeWon.amount} XP multiplier has been applied.
                    It will expire on {moment(prizeWon.expiresAt).format("LLL")}.
                </div>
            </div>
        );
    }

    function getRewardImageUrl(): string {
        return redemptionState === RewardNftRedemptionState.NOT_INITIATED || !rewardMetadata.redemptionAnimation
            ? rewardImage
            : rewardMetadata.redemptionAnimation.url;
    }

    if (!nftMetadata) {
        return (
            <div className="d-flex justify-content-center">
                <div className="spinner"/>
            </div>
        );
    }

    return (
        <>
            <Toast
                message="Please DO NOT refresh! We are getting your prize ready."
                isOpen={showToast}
                showButton={false}
                onDismiss={handleToastDismissal}
                variant="warning"
                remainIndefinitely={true}
                removeMarginAdjustment={true}
            />
            <div className="govip-rewards-drawer_reward">
                <div className="govip-rewards-drawer_reward_top">
                    <div
                        className="govip-rewards-drawer_reward_top_image-containerreward-image show"
                    >
                        <img src={getRewardImageUrl()} alt="NFT Reward"/>

                        <img
                            src={`${import.meta.env.VITE_CDN_URL}/images/govip-rewards-light-effect.gif`}
                            alt="Light Effect"
                            className={classNames("light-effect", {
                                show: revealPrize && !prizeWon && rewardMetadata.transitionEffectEnabled,
                            })}
                        />
                    </div>

                </div>
                {prizeWon
                    ? renderPrizeInfo()
                    : <div className="govip-rewards-drawer_reward_info">
                        <div className="govip-rewards-drawer_reward_info_origin">
                            <span>{rewardMetadata.origin}</span>
                        </div>
                        <div
                            className="govip-rewards-drawer_reward_info_title">{nftMetadata.tokenName}</div>
                        <div
                            className="govip-rewards-drawer_reward_info_nftId">#{props.ownership.nftId}</div>
                        <div
                            className="govip-rewards-drawer_reward_info_description">{nftMetadata.tokenDescription}</div>
                    </div>
                }
            </div>

            <div className="govip-rewards-drawer_footer">
                <GoVipRewardsSelectedXpMultiplierFooter
                    redemptionState={redemptionState}
                    toggleDrawer={props.toggleDrawer}
                    ownership={props.ownership}
                    contractAddress={rewardMetadata.nftTracker.contractAddress}
                    onVirtualRedemption={onVirtualRedemption}
                    onTransactionStarted={onConfirmRedeem}
                    onTransactionSubmit={onTransactionSent}
                    onTransactionConfirmed={onTransactionConfirmed}
                    relayerUrl={rewardMetadata.relayerUrl}
                    forwarderAddress={rewardMetadata.forwarderAddress}
                    chain={parseChain(rewardMetadata.nftTracker.evmChain)}
                />
            </div>
        </>
    );
}

export default GoVipRewardsSelectedXpMultiplier;
